import React from 'react';
import { BookOpen } from 'lucide-react';
import PortfolioHeader from './PortfolioHeader';
import YearSection from './YearSection';
import EmptyState from '../../parentPortal/components/EmptyState';
import ErrorBlock from '../../parentPortal/components/ErrorBlock';
import { Skeleton, SkeletonPanel, SkeletonRegion } from '../../../components/ui/Skeleton';

// Shared view used by both /insights/portfolio (learner) and
// /family/students/:id/portfolio (parent). `subjectFallbackName` is displayed
// when portfolio is empty (e.g. "your" vs. "Emma's").
export default function PortfolioView({
  portfolio,
  loading,
  error,
  onReload,
  subjectFallbackName = 'This',
  onExport,
  showIdentity = true,
}) {
  if (loading) {
    return (
      <SkeletonRegion label="Loading transcript" style={{ display: 'grid', gap: 8 }}>
        {/* PortfolioHeader stand-in: identity line + aggregate stat row. */}
        <SkeletonPanel style={{ marginBottom: 'var(--space-4)' }}>
          <Skeleton width="38%" height={22} style={{ marginBottom: 'var(--space-4)' }} />
          <div style={{ display: 'flex', gap: 'var(--space-6)' }}>
            {Array.from({ length: 3 }, (_, i) => (
              <div key={i}>
                <Skeleton width={48} height={20} style={{ marginBottom: 6 }} />
                <Skeleton width={84} height={11} />
              </div>
            ))}
          </div>
        </SkeletonPanel>

        {/* Two academic-year sections, each with a few course rows. */}
        {Array.from({ length: 2 }, (_, y) => (
          <div key={y} style={{ marginBottom: 'var(--space-5)' }}>
            <Skeleton width="24%" height={16} style={{ marginBottom: 'var(--space-3)' }} />
            {Array.from({ length: 3 }, (_, r) => (
              <div key={r} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', padding: 'var(--space-3) 0' }}>
                <Skeleton height={14} style={{ flex: 1, maxWidth: `${70 - r * 8}%` }} />
                <Skeleton width={54} height={20} />
              </div>
            ))}
          </div>
        ))}
      </SkeletonRegion>
    );
  }
  if (error) return <ErrorBlock message={error} onRetry={onReload} />;
  if (!portfolio) return null;

  const years = portfolio.academicYears || [];
  const totalCourses =
    (portfolio.aggregate?.totalCoursesCompleted || 0) +
    (portfolio.aggregate?.totalCoursesInProgress || 0);

  return (
    <div style={{ display: 'grid', gap: 8 }}>
      <PortfolioHeader portfolio={portfolio} onExport={onExport} showIdentity={showIdentity} />

      {totalCourses === 0 || years.length === 0 ? (
        <div style={{ marginTop: 16 }}>
          <EmptyState
            icon={<BookOpen size={22} />}
            title="Nothing recorded yet"
            description={`${subjectFallbackName} transcript will build itself as courses are started and completed.`}
          />
        </div>
      ) : (
        years.map((y) => <YearSection key={y.year} year={y} />)
      )}
    </div>
  );
}
